import React from "react";

import { css } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod/v4-mini";

import { useStyles } from "@/hooks/useStyles.ts";
import {
  RefuseReinclusionReason,
  refuseReinclusionReasonTranslationMapping,
} from "@/models/ReinclusionAgentSuggestionModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { SelectItem } from "@/uiKit/atoms/inputs/SelectItem.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { ControlledTextField } from "@/uiKit/molecules/ControlledTextField.tsx";
import { FormSelectInput } from "@/uiKit/molecules/form/FormSelectInput.tsx";

export type RefuseReinclusionSuggestionModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: RefuseReinclusionReason, reasonFreeText: string | undefined) => void;
  isPending?: boolean;
};

type RefuseReinclusionFormData =
  | {
      reason: "no_clinical_need" | "non_compliant";
      reasonFreeText?: string;
    }
  | {
      reason: "other";
      reasonFreeText: string;
    };

const refuseReinclusionFormSchema = z.discriminatedUnion("reason", [
  z.object({
    reason: z.literal("no_clinical_need"),
    reasonFreeText: z.optional(z.string()),
  }),
  z.object({
    reason: z.literal("non_compliant"),
    reasonFreeText: z.optional(z.string()),
  }),
  z.object({
    reason: z.literal("other"),
    reasonFreeText: z.string(),
  }),
]);

const reinclusionReasons = {
  no_clinical_need: undefined,
  non_compliant: undefined,
  other: undefined,
} satisfies Record<RefuseReinclusionReason, undefined>;

export const RefuseReinclusionSuggestionModal: React.FC<RefuseReinclusionSuggestionModalProps> = ({
  open,
  onClose,
  onSubmit,
  isPending = false,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const formContext = useForm<RefuseReinclusionFormData>({
    resolver: zodResolver(refuseReinclusionFormSchema),
    defaultValues: { reason: "no_clinical_need" },
  });

  const reason = useWatch({ control: formContext.control, name: "reason" });
  const isOtherSelected = reason === "other";

  return (
    <Dialog id="refuse-reinclusion-dialog" open={open} onClose={onClose} maxWidth="sm">
      <Form formContext={formContext} onSubmit={data => onSubmit(data.reason, data.reasonFreeText)}>
        <CardHeader
          id="refuse-reinclusion-header"
          title={t("reinclusion.agent.refuseModal.title")}
          onExit={onClose}
          variant="contained"
        />
        <CardContent className={styles.content}>
          <Typography variant="body">{t("reinclusion.agent.refuseModal.subtitle")}</Typography>
          <FormSelectInput id="refuse-reinclusion-reason" name="reason" fullWidth reserveErrorSpace>
            {Object.keys(reinclusionReasons).map(key => (
              <SelectItem key={key} value={key}>
                {t(refuseReinclusionReasonTranslationMapping(key))}
              </SelectItem>
            ))}
          </FormSelectInput>
          {isOtherSelected && (
            <ControlledTextField
              id="refuse-reinclusion-reason-free-text"
              name="reasonFreeText"
              multiline
              required
              placeholder={t("reinclusion.agent.refuseModal.otherPlaceholder")}
            />
          )}
        </CardContent>
        <CardFooter className={styles.footer}>
          <Button variant="outlined" data-action="inclusion.reinclusion.cancel" onClick={onClose} disabled={isPending}>
            {t("reinclusion.agent.refuseModal.cancel")}
          </Button>
          <Button
            id="refuse-reinclusion-submit"
            variant="contained"
            data-action="inclusion.reinclusion.reject"
            type="submit"
            pending={isPending}
          >
            {t("reinclusion.agent.refuseModal.submit")}
          </Button>
        </CardFooter>
      </Form>
    </Dialog>
  );
};

const makeStyles = (theme: Theme) => ({
  form: css({
    display: "flex",
    flexDirection: "column",
  }),
  content: css({
    paddingTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
  }),
  footer: css({
    paddingBottom: theme.spacing(8),
    gap: theme.spacing(8),
  }),
});
